// The single "does this route need a chosen library" decision (Paul, 15
// September 2026: "you should not be able to open anything until you have
// chosen a library"). Shared by app.ts's route-level gate, which decides
// whether a partner-scoped page ever gets constructed, and cos-app-shell.ts's
// own _awaitingClient(), which decides whether to render the "Choose a
// library" prompt in place of the (otherwise empty) <slot>. Both have to
// agree, so the split lives in exactly one place rather than two copies that
// could drift.
//
// libraryGateBlocks() takes the chosen id as a parameter rather than reading
// legacyClientId() itself: cos-app-shell keeps its own `_legacyClientId`
// state (synced off the `cos:legacy-client-changed` window event) and passes
// that, while app.ts reads services/legacy-client.ts's legacyClientId()
// directly since it has no state of its own to mirror it into. A caller that
// wants the live sessionStorage value can always pass legacyClientId()
// itself.

// The Partner/Communico workspace split. Moved out of cos-app-shell.ts (which
// remains the sole owner of the workspace tab switcher itself) purely so
// app.ts's route guard can read the same lists without importing the whole
// shell component. See cos-app-shell.ts's own history comments above its
// (re-exported) COMMUNICO_ONLY_PREFIXES for why each entry is there.
export const PARTNER_SIDE_PREFIXES = [
	'/circulate', '/lend', '/preserve', '/roster', '/thrive', '/measure',
	'/nurture', '/broadcast', '/attend', '/reserve', '/outreach', '/bookmobile',
	'/collections', '/partner-admin', '/media', '/report-builder', '/desk',
	'/action-center', '/communications', '/explore', '/schedule', '/annual-report',
];

export const COMMUNICO_ONLY_PREFIXES = [
	'/inbox', '/partners', '/deals', '/projects', '/prospects', '/billing',
	'/time-off', '/files', '/conversations', '/mail', '/calendar', '/admin',
	'/activity', '/my-work', '/tasks',
];

/** Library staff (level 3+) have their own tenant and no picker; only
 *  Communico staff (level 1 or 2, see docs/architecture/users-and-
 *  permissions.md on level 2, "Communico staff, reduced") ever need one. */
export function isCommunicoStaffLevel(level: number): boolean {
	return level < 3;
}

/**
 * Whether `path` is a partner-scoped surface for gating purposes. `/kb` and
 * `/community` are workspace-ambiguous and count only while the partner view
 * is actually active (`?view=partner`): matching them unconditionally would
 * gate the Communico-side KB/Community too. `/community` is matched EXACTLY
 * (never startsWith) so it can't catch `/communications` (Comms Hub), which
 * stays Communico-side, mirroring cos-app-shell.ts's own willUpdate check.
 */
export function isPartnerSidePath(path: string, search: string): boolean {
	if (PARTNER_SIDE_PREFIXES.some((p) => path.startsWith(p))) return true;
	if (path.startsWith('/kb') || path === '/community') {
		return new URLSearchParams(search).get('view') === 'partner';
	}
	return false;
}

/**
 * Whether a partner-side route must be blocked until a library is chosen.
 * `chosenId` is 0 meaning nothing chosen (services/legacy-client.ts's
 * legacyClientId() convention), a plain sessionStorage read, no fetch, no
 * loaded list to wait on, so the decision is knowable before the very first
 * render, not merely "on first paint": there is no earlier moment for a race
 * to lose. A staffer with exactly one available library is auto-picked into
 * it (see ensureLegacyClientAutoPicked in services/legacy-client.ts);
 * callers that need that pick to have already landed before deciding (app.ts's
 * route guards) await it first.
 */
export function libraryGateBlocks(path: string, search: string, userLevel: number, chosenId: number): boolean {
	return isCommunicoStaffLevel(userLevel) && isPartnerSidePath(path, search) && chosenId === 0;
}
